import { useSuspenseQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { createColumnHelper, type SortingState } from '@tanstack/react-table';
import {
  format,
  getDay,
  isAfter,
  isBefore,
  parseISO,
  startOfDay,
} from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { getActiveClassQueryOptions } from '~entities/classes/api/get-active-class';
import { useGetActiveClassAttendanceByDate } from '~entities/classes/api/get-active-class-attendance';
import { useMarkAllAttendanceMutation } from '~entities/classes/api/mark-all-class-attendance';
import type { MakeupBooking } from '~entities/makeup/api/book-makeup';
import { useGetMakeupSettings } from '~entities/makeup/api/get-makeup-settings';
import { useGetMakeupsBySource } from '~entities/makeup/api/get-makeups-by-source';
import type { Student } from '~entities/student';
import {
  type TrialStudent,
  useGetAllTrialStudents,
} from '~entities/student/api/get-all-trial-students';

import {
  type AttendanceNameSortField,
  compareAttendanceStudentsByName,
  getAttendanceSortFirstName,
  getAttendanceSortLastName,
  toTrialAttendanceSortParts,
} from '~features/classes/lib/attendance-student';
import { MakeupStudentTable } from '~features/classes/ui/makeup-student-table';
import { ManageMakeupSheet } from '~features/classes/ui/manage-makeup-sheet';
import { ScheduleMakeupSheet } from '~features/classes/ui/schedule-makeup-sheet';
import { PresentStatusSelector } from '~features/shared/present-status-selector';
import { StatusSelectorForTrialStudents } from '~features/shared/status-selector-for-trial-students';

import {
  EnrollioTable,
  EnrollioTableSearch,
} from '~shared/components/enrollio-table';
import { MedicalDetailsCard } from '~shared/components/medical-details';
import { DataTableColumnHeader } from '~shared/components/table/header';
import type { Days } from '~shared/constants/days';
import { parseToNoonDate } from '~shared/helpers/datetime';
import { Calendar } from '~shared/shadcn/calendar';
import { cn } from '~shared/shadcn/lib/utils';
import { Badge } from '~shared/shadcn/ui/badge';
import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~shared/shadcn/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';
import { getErrorMessage } from '~shared/utils/error-handler';

import { AttendanceStudentCell } from './attendance-student-cell';

const DAYS_OF_WEEK = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
} as const;

const ATTENDANCE_TYPE = [
  { id: 'all', label: 'Show All' },
  { id: 'all-absent', label: 'All Absent' },
  { id: 'all-present', label: 'All Present' },
] as const;

function getMakeupStudentId(booking: MakeupBooking): string {
  return typeof booking.studentId === 'string'
    ? booking.studentId
    : booking.studentId._id;
}

function formatMakeupPillDate(value: string): string {
  try {
    return format(parseISO(String(value).slice(0, 10)), 'MM/dd/yyyy');
  } catch {
    return String(value).slice(0, 10);
  }
}

function SpentAbsenceBadge({
  booking,
  onManage,
}: {
  booking: MakeupBooking;
  onManage?: (booking: MakeupBooking) => void;
}) {
  const { status } = booking;

  if (status === 'completed') {
    return (
      <Badge className='shrink-0 whitespace-nowrap border-transparent bg-emerald-100 text-emerald-800 hover:bg-emerald-100'>
        Made up
      </Badge>
    );
  }
  if (status === 'no_show') {
    return (
      <Badge className='shrink-0 whitespace-nowrap border-transparent bg-sky-100 text-sky-800 hover:bg-sky-100'>
        No Show
      </Badge>
    );
  }
  if (status === 'scheduled' || status === 'requested') {
    const label = `Makeup ${formatMakeupPillDate(booking.targetDate)}`;
    if (onManage) {
      return (
        <Button
          type='button'
          size='sm'
          variant='secondary'
          className='h-7 shrink-0 whitespace-nowrap px-2.5 text-xs font-medium'
          onClick={() => {
            onManage(booking);
          }}
        >
          {label}
        </Button>
      );
    }
    return (
      <Badge className='shrink-0 whitespace-nowrap border-transparent bg-amber-100 text-amber-900 hover:bg-amber-100'>
        {label}
      </Badge>
    );
  }
  return null;
}

/** Absence is still spent / not rebookable for these statuses. */
function isAbsenceSpentStatus(status: MakeupBooking['status']): boolean {
  return (
    status === 'requested' ||
    status === 'scheduled' ||
    status === 'completed' ||
    status === 'no_show'
  );
}

// Get all day numbers (0-6)
type DayNumbers = (typeof DAYS_OF_WEEK)[keyof typeof DAYS_OF_WEEK];

function createDisabledDates({
  dayNames,
  startDate,
  endDate,
}: {
  dayNames: Array<Days>;
  startDate: string | Date | null | undefined;
  endDate: string | Date | null | undefined;
}): (day: Date) => boolean {
  // Convert day names to a Set of day numbers for fast lookup
  const allowedDays = new Set(dayNames.map((day) => DAYS_OF_WEEK[day]));

  // Class dates arrive as UTC midnight; read the calendar day only so negative
  // offsets don't shift the selectable range by a day.
  const parsedStart = parseToNoonDate(startDate);
  const parsedEnd = parseToNoonDate(endDate);

  if (!parsedStart || !parsedEnd) {
    return () => true;
  }

  const start = startOfDay(parsedStart);
  const end = startOfDay(parsedEnd);

  // Allow past / today / future class meeting days (ENR-3805). Non-class days
  // and dates outside the class window stay disabled (ENR-637).
  return (day: Date): boolean => {
    const normalizedDay = startOfDay(day);

    return (
      !allowedDays.has(getDay(day) as DayNumbers) ||
      isBefore(normalizedDay, start) ||
      isAfter(normalizedDay, end)
    );
  };
}

function getNearestPrevDate(dayNames: Array<Days>, fromDate: Date): Date {
  // Convert dayNames to day numbers
  const targetDays = new Set(dayNames.map((day) => DAYS_OF_WEEK[day]));

  // Start from the given date
  const resultDate = new Date(fromDate);

  // Keep going back one day at a time until we find a match
  while (!targetDays.has(resultDate.getDay() as 0 | 1 | 2 | 3 | 4 | 5 | 6)) {
    resultDate.setDate(resultDate.getDate() - 1);
  }

  return resultDate;
}

export const StudentAttendanceAll = () => {
  const { classId } = useParams({
    from: '/_auth/_layout/attendance/$classId',
  });

  const { data } = useSuspenseQuery(getActiveClassQueryOptions(classId));

  const { defaultDate, disabledDates, hasSchedule } = useMemo(() => {
    const classDaysNames = data.days;
    if (!classDaysNames.length || !data.startDate || !data.endDate) {
      return {
        defaultDate: new Date(),
        disabledDates: () => true,
        hasSchedule: false,
      };
    }

    return {
      defaultDate: getNearestPrevDate(classDaysNames, new Date()),
      disabledDates: createDisabledDates({
        dayNames: classDaysNames,
        startDate: data.startDate,
        endDate: data.endDate,
      }),
      hasSchedule: true,
    };
  }, [data.days, data.endDate, data.startDate]);

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [showDatePicker, setShowDatePicker] = useState(false);

  const date = selectedDate ?? defaultDate;
  const formattedDate = format(date, 'yyyy-MM-dd');
  const { data: makeupSettings } = useGetMakeupSettings();
  const makeupEnabled = makeupSettings?.makeupEnabled ?? false;
  const sessionId = data.session?._id;

  if (!hasSchedule) {
    return (
      <div className='px-2 py-4 sm:px-4 sm:py-6'>
        <div className='rounded-md border border-dashed p-8 text-center'>
          <h2 className='text-lg font-semibold'>
            Class schedule is incomplete
          </h2>
          <p className='mt-1 text-sm text-muted-foreground'>
            Attendance cannot be loaded without class days and dates.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className='px-2 py-4 sm:px-4 sm:py-6'>
      <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
        <h2 className='text-lg font-bold sm:text-2xl'>
          {format(date, 'EEEE, MMMM d, yyyy')}
        </h2>
        <div className='flex items-center gap-2'>
          <span className='hidden text-sm text-muted-foreground sm:inline'>
            Select date:
          </span>
          {/* <Input
            type='date'
            value={formattedDate}
            className='h-9 w-[140px]'
            onChange={(e) => {
              if (!e.target.value) {
                return;
              }
              const newDate = new Date(e.target.value);
              setSelectedDate(newDate);
            }}
          /> */}
          <Popover open={showDatePicker} onOpenChange={setShowDatePicker}>
            <PopoverTrigger asChild>
              <Button
                variant='outline'
                className={cn('w-52 justify-start text-left font-normal')}
              >
                <CalendarIcon className='mr-2 size-4' />
                {format(date, 'PPP')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className='w-auto p-0' align='start'>
              <Calendar
                mode='single'
                selected={date}
                onSelect={(e) => {
                  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                  if (e) {
                    setSelectedDate(e);
                    setShowDatePicker(false);
                  }
                }}
                disabled={disabledDates}
                required
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <div className='mt-4 space-y-6'>
        <TrialStudentTable classId={classId} formattedDate={formattedDate} />
        <RegularStudentTable
          classId={classId}
          formattedDate={formattedDate}
          makeupEnabled={makeupEnabled}
          sessionId={sessionId}
          sendConfirmationDefault={
            makeupSettings?.makeupConfirmationEmailEnabled ?? true
          }
        />
        <MakeupStudentTable
          classId={classId}
          formattedDate={formattedDate}
          enabled={makeupEnabled}
        />
      </div>
    </div>
  );
};

const regularStudentTableColumnHelper = createColumnHelper<Student>();
interface RegularStudentTableProps {
  classId: string;
  formattedDate: string;
  makeupEnabled?: boolean;
  sessionId?: string;
  sendConfirmationDefault?: boolean;
}

function RegularStudentTable({
  classId,
  formattedDate,
  makeupEnabled = false,
  sessionId = '',
  sendConfirmationDefault = true,
}: RegularStudentTableProps) {
  const [selectedAttendanceType, setSelectedAttendanceType] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [nameSortField, setNameSortField] =
    useState<AttendanceNameSortField>('lastName');
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'student', desc: false },
  ]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 100,
  });
  const [showMarkAllDialog, setShowMarkAllDialog] = useState(false);
  const [markAllType, setMarkAllType] = useState<'present' | 'absent' | null>(
    null,
  );
  const [makeupStudent, setMakeupStudent] = useState<Student | null>(null);
  // Snapshot absence date when opening the sheet so changing the calendar
  // while the sheet is open does not rebind the source attendance date.
  const [makeupSourceDate, setMakeupSourceDate] = useState<string | null>(null);
  const [manageBooking, setManageBooking] = useState<MakeupBooking | null>(
    null,
  );
  const [manageStudent, setManageStudent] = useState<Student | null>(null);

  const openManageMakeup = (student: Student, booking: MakeupBooking) => {
    setManageStudent(student);
    setManageBooking(booking);
  };

  const { data: attendanceData } = useGetActiveClassAttendanceByDate({
    classId: classId,
    date: formattedDate,
  });

  const { data: sourceMakeups = [] } = useGetMakeupsBySource(
    { enrollmentId: classId, date: formattedDate },
    makeupEnabled,
  );

  const spentAbsenceByStudentId = useMemo(() => {
    const map = new Map<string, MakeupBooking>();
    for (const booking of sourceMakeups) {
      // Cancelled/expired free the absence for rebooking — don't hide Schedule.
      if (!isAbsenceSpentStatus(booking.status)) continue;
      const studentId = getMakeupStudentId(booking);
      const existing = map.get(studentId);
      if (!existing) {
        map.set(studentId, booking);
        continue;
      }
      // Prefer the newest booking if multiple somehow exist.
      const existingCreated = Date.parse(existing.createdAt ?? '') || 0;
      const bookingCreated = Date.parse(booking.createdAt ?? '') || 0;
      if (bookingCreated >= existingCreated) {
        map.set(studentId, booking);
      }
    }
    return map;
  }, [sourceMakeups]);

  const tableData = useMemo(() => {
    if (!attendanceData) {
      return [];
    }
    const query = searchQuery.trim().toLowerCase();
    return attendanceData.filter((student) => {
      const currentAttendance = student.attendance[0]?.attendanceId;

      switch (selectedAttendanceType) {
        case 'all-absent':
          if (currentAttendance?.isPresent !== false) return false;
          break;
        case 'all-present':
          if (currentAttendance?.isPresent !== true) return false;
          break;
        default:
          break;
      }

      if (!query) return true;
      const haystack = [
        student.name,
        student.firstName,
        student.lastName,
        student.familyName,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [attendanceData, searchQuery, selectedAttendanceType]);

  const mobileSortedData = useMemo(() => {
    const rows = [...tableData];
    const descending = sorting[0]?.desc ?? false;
    rows.sort((a, b) => {
      const cmp = compareAttendanceStudentsByName(a, b, nameSortField);
      return descending ? -cmp : cmp;
    });
    return rows;
  }, [nameSortField, sorting, tableData]);

  const cols = useMemo(
    () => [
      regularStudentTableColumnHelper.accessor(
        (row) =>
          nameSortField === 'lastName'
            ? getAttendanceSortLastName(row)
            : getAttendanceSortFirstName(row),
        {
          id: 'student',
          header: (info) => (
            <DataTableColumnHeader
              column={info.column}
              title='Student'
              className='pl-4 text-black'
            />
          ),
          // Sort from row.original so first/last field changes are not stuck
          // on TanStack's per-column _valuesCache (ENR-3438 prod smoke).
          sortingFn: (rowA, rowB) =>
            compareAttendanceStudentsByName(
              rowA.original,
              rowB.original,
              nameSortField,
            ),
          cell: ({ row }) => (
            <AttendanceStudentCell
              student={row.original}
              attendanceDate={formattedDate}
            />
          ),
        },
      ),
      regularStudentTableColumnHelper.accessor('attendance', {
        header: 'Action',
        enableSorting: false,
        cell: (info) => {
          const student = info.row.original;
          const isAbsent =
            student.attendance[0]?.attendanceId?.isPresent === false;
          const spentMakeup = spentAbsenceByStudentId.get(student._id);

          return (
            <div className='flex min-w-0 flex-nowrap items-center gap-2'>
              <PresentStatusSelector
                data={student}
                classId={classId}
                date={formattedDate}
              />
              {makeupEnabled && isAbsent && sessionId ? (
                spentMakeup ? (
                  <SpentAbsenceBadge
                    booking={spentMakeup}
                    onManage={(booking) => {
                      openManageMakeup(student, booking);
                    }}
                  />
                ) : (
                  <Button
                    size='sm'
                    className='shrink-0 whitespace-nowrap bg-secondary text-secondary-foreground hover:bg-secondary/90'
                    onClick={() => {
                      setMakeupStudent(student);
                      setMakeupSourceDate(formattedDate);
                    }}
                  >
                    Schedule Makeup
                  </Button>
                )
              ) : null}
            </div>
          );
        },
      }),
    ],
    [
      classId,
      formattedDate,
      makeupEnabled,
      nameSortField,
      sessionId,
      spentAbsenceByStudentId,
    ],
  );

  const markAllMutation = useMarkAllAttendanceMutation();

  const handleMarkAll = async () => {
    if (!markAllType) return;

    try {
      await markAllMutation.mutateAsync({
        classId,
        date: formattedDate,
        markAllType,
      });
      toast.success(`All students marked as ${markAllType}.`);
      setShowMarkAllDialog(false);
      setMarkAllType(null);
    } catch (error) {
      console.error('Failed to mark all:', error);
      toast.error(
        getErrorMessage(
          error,
          `Failed to mark all students as ${markAllType}.`,
        ),
      );
    }
  };

  return (
    <div className=''>
      <h2 className='mb-4 text-xl font-semibold'>Regular Students</h2>

      {/* Mobile view */}
      <div className='md:hidden'>
        <div className='mb-4 flex flex-col gap-3'>
          <EnrollioTableSearch
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder='Search students...'
          />
          <div className='flex flex-col gap-3 sm:flex-row sm:items-center'>
            <Select
              value={selectedAttendanceType}
              onValueChange={(value) => {
                if (value === 'All') {
                  setSelectedAttendanceType('');
                  return;
                }

                setSelectedAttendanceType(value);
              }}
            >
              <SelectTrigger className='h-9 w-full bg-secondary text-sm text-secondary-foreground sm:w-[180px] md:w-[200px]'>
                <SelectValue placeholder='Show All' />
              </SelectTrigger>
              <SelectContent>
                {ATTENDANCE_TYPE.map((type) => (
                  <SelectItem key={type.id} value={type.id}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={nameSortField}
              onValueChange={(value) => {
                setNameSortField(value as AttendanceNameSortField);
                setSorting([{ id: 'student', desc: false }]);
              }}
            >
              <SelectTrigger className='h-9 w-full bg-secondary text-sm text-secondary-foreground sm:w-[200px]'>
                <SelectValue placeholder='Sort by last name' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='lastName'>Sort by last name</SelectItem>
                <SelectItem value='firstName'>Sort by first name</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className='flex gap-2'>
            <Button
              className='h-9 flex-1 px-3 text-xs sm:w-auto sm:flex-none sm:text-sm'
              variant='outline'
              size='sm'
              onClick={() => {
                setMarkAllType('present');
                setShowMarkAllDialog(true);
              }}
            >
              Mark All Present
            </Button>
            <Button
              className='h-9 flex-1 px-3 text-xs sm:w-auto sm:flex-none sm:text-sm'
              variant='outline'
              size='sm'
              onClick={() => {
                setMarkAllType('absent');
                setShowMarkAllDialog(true);
              }}
            >
              Mark All Absent
            </Button>
          </div>
        </div>

        {mobileSortedData.map((student) => {
          const isAbsent =
            student.attendance[0]?.attendanceId?.isPresent === false;
          const spentMakeup = spentAbsenceByStudentId.get(student._id);
          return (
            <div key={student._id} className='mb-2 rounded-md border p-3'>
              <div className='mb-3'>
                <AttendanceStudentCell
                  student={student}
                  attendanceDate={formattedDate}
                />
              </div>
              <div className='flex min-w-0 flex-nowrap items-center gap-2'>
                <PresentStatusSelector
                  data={student}
                  classId={classId}
                  date={formattedDate}
                />
                {makeupEnabled && isAbsent && sessionId ? (
                  spentMakeup ? (
                    <SpentAbsenceBadge
                      booking={spentMakeup}
                      onManage={(booking) => {
                        openManageMakeup(student, booking);
                      }}
                    />
                  ) : (
                    <Button
                      size='sm'
                      className='shrink-0 whitespace-nowrap bg-secondary text-secondary-foreground hover:bg-secondary/90'
                      onClick={() => {
                        setMakeupStudent(student);
                        setMakeupSourceDate(formattedDate);
                      }}
                    >
                      Schedule Makeup
                    </Button>
                  )
                ) : null}
              </div>
            </div>
          );
        })}

        <div className='mt-4 text-sm text-muted-foreground'>
          Showing {mobileSortedData.length} of {mobileSortedData.length}{' '}
          Students
        </div>
      </div>

      {/* Desktop view */}
      <div className='hidden md:block'>
        <EnrollioTable
          ariaLabel='Class attendance students'
          totalLabel='Students'
          filterHeader={
            <>
              <EnrollioTableSearch
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder='Search students...'
              />
              <Select
                value={selectedAttendanceType}
                onValueChange={(value) => {
                  if (value === 'All') {
                    setSelectedAttendanceType('');
                    return;
                  }

                  setSelectedAttendanceType(value);
                }}
              >
                <SelectTrigger className='h-9 w-full bg-secondary text-sm text-secondary-foreground sm:w-[180px] md:w-[200px]'>
                  <SelectValue placeholder='Show All' />
                </SelectTrigger>
                <SelectContent>
                  {ATTENDANCE_TYPE.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={nameSortField}
                onValueChange={(value) => {
                  setNameSortField(value as AttendanceNameSortField);
                  setSorting([{ id: 'student', desc: false }]);
                }}
              >
                <SelectTrigger className='h-9 w-full bg-secondary text-sm text-secondary-foreground sm:w-[200px]'>
                  <SelectValue placeholder='Sort by last name' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='lastName'>Sort by last name</SelectItem>
                  <SelectItem value='firstName'>Sort by first name</SelectItem>
                </SelectContent>
              </Select>
            </>
          }
          toolbar={
            <div className='flex gap-2'>
              <Button
                className='h-9 flex-1 px-3 text-xs sm:w-auto sm:flex-none sm:text-sm'
                variant='outline'
                size='sm'
                onClick={() => {
                  setMarkAllType('present');
                  setShowMarkAllDialog(true);
                }}
              >
                Mark All Present
              </Button>
              <Button
                className='h-9 flex-1 px-3 text-xs sm:w-auto sm:flex-none sm:text-sm'
                variant='outline'
                size='sm'
                onClick={() => {
                  setMarkAllType('absent');
                  setShowMarkAllDialog(true);
                }}
              >
                Mark All Absent
              </Button>
            </div>
          }
          columns={cols}
          data={mobileSortedData}
          getRowId={(row) => row._id}
          sorting={sorting}
          onSortingChange={setSorting}
          manualSorting
          pagination={pagination}
          onPaginationChange={setPagination}
          mobile={false}
        />
      </div>

      <MedicalDetailsCard classId={classId} className='mt-6' />

      <Dialog open={showMarkAllDialog} onOpenChange={setShowMarkAllDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Action</DialogTitle>
          </DialogHeader>
          <DialogDescription>
            Are you sure you want to mark all students as {markAllType}? This
            will override any existing attendance records for this date.
          </DialogDescription>
          <DialogFooter className='mt-4 gap-2'>
            <Button
              variant='outline'
              onClick={() => {
                setShowMarkAllDialog(false);
                setMarkAllType(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleMarkAll}
              disabled={markAllMutation.isPending}
            >
              {markAllMutation.isPending ? 'Updating...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ScheduleMakeupSheet
        open={!!makeupStudent}
        onOpenChange={(open) => {
          if (!open) {
            setMakeupStudent(null);
            setMakeupSourceDate(null);
          }
        }}
        student={makeupStudent}
        sourceEnrollmentId={classId}
        sourceAttendanceDate={makeupSourceDate ?? formattedDate}
        sessionId={sessionId}
        sendConfirmationDefault={sendConfirmationDefault}
      />
      <ManageMakeupSheet
        open={!!manageBooking}
        onOpenChange={(open) => {
          if (!open) {
            setManageBooking(null);
            setManageStudent(null);
          }
        }}
        booking={manageBooking}
        student={manageStudent}
        sessionId={sessionId}
        sendConfirmationDefault={sendConfirmationDefault}
      />
    </div>
  );
}

// Mobile view for trial students
const TrialStudentCard = ({
  student,
  classId,
  formattedDate,
}: {
  student: TrialStudent;
  classId: string;
  formattedDate: string;
}) => (
  <div key={student._id} className='mb-2 rounded-md border p-3'>
    <div className='mb-2 font-medium'>{student.name}</div>
    <StatusSelectorForTrialStudents
      data={student}
      classId={classId}
      date={formattedDate}
    />
  </div>
);

const trialStudentTableColumnHelper = createColumnHelper<TrialStudent>();

function TrialStudentTable({
  classId,
  formattedDate,
}: RegularStudentTableProps) {
  const { data } = useGetAllTrialStudents({ classId, date: formattedDate });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAttendanceType, setSelectedAttendanceType] = useState('');
  const [nameSortField, setNameSortField] =
    useState<AttendanceNameSortField>('lastName');
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'student', desc: false },
  ]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 100,
  });

  const cols = useMemo(
    () => [
      trialStudentTableColumnHelper.accessor(
        (row) => {
          const parts = toTrialAttendanceSortParts(row);
          return nameSortField === 'lastName'
            ? getAttendanceSortLastName(parts)
            : getAttendanceSortFirstName(parts);
        },
        {
          id: 'student',
          header: (info) => (
            <DataTableColumnHeader
              column={info.column}
              title='Student'
              className='pl-4 text-black'
            />
          ),
          sortingFn: (rowA, rowB) =>
            compareAttendanceStudentsByName(
              toTrialAttendanceSortParts(rowA.original),
              toTrialAttendanceSortParts(rowB.original),
              nameSortField,
            ),
          cell: ({ row }) => (
            <div className='flex items-center space-x-4'>
              <div>
                <div className='font-medium'>
                  {row.original.studentName || row.original.name || ''}
                </div>
              </div>
            </div>
          ),
        },
      ),
      trialStudentTableColumnHelper.display({
        header: 'Action',
        id: 'action',
        enableSorting: false,
        cell: (info) => {
          return (
            <StatusSelectorForTrialStudents
              data={info.row.original}
              classId={classId}
              date={formattedDate}
            />
          );
        },
      }),
    ],
    [classId, formattedDate, nameSortField],
  );

  const tableData = useMemo(() => {
    const rows = data?.data ?? [];
    const query = searchQuery.trim().toLowerCase();
    return rows.filter((student) => {
      switch (selectedAttendanceType) {
        case 'all-absent':
          if (student.isPresent !== false) return false;
          break;
        case 'all-present':
          if (student.isPresent !== true) return false;
          break;
        default:
          break;
      }

      if (!query) return true;
      const haystack = [
        student.studentName,
        student.name,
        student.firstName,
        student.lastName,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [data?.data, searchQuery, selectedAttendanceType]);

  const mobileSortedData = useMemo(() => {
    const rows = [...tableData];
    const descending = sorting[0]?.desc ?? false;
    rows.sort((a, b) => {
      const cmp = compareAttendanceStudentsByName(
        toTrialAttendanceSortParts(a),
        toTrialAttendanceSortParts(b),
        nameSortField,
      );
      return descending ? -cmp : cmp;
    });
    return rows;
  }, [nameSortField, sorting, tableData]);

  const hasAnyTrials = (data?.data ?? []).length > 0;

  const attendanceTypeSelect = (
    <Select
      value={selectedAttendanceType}
      onValueChange={(value) => {
        if (value === 'all') {
          setSelectedAttendanceType('');
          return;
        }
        setSelectedAttendanceType(value);
      }}
    >
      <SelectTrigger className='h-9 w-full bg-secondary text-sm text-secondary-foreground sm:w-[180px] md:w-[200px]'>
        <SelectValue placeholder='Show All' />
      </SelectTrigger>
      <SelectContent>
        {ATTENDANCE_TYPE.map((type) => (
          <SelectItem key={type.id} value={type.id}>
            {type.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const nameSortSelect = (
    <Select
      value={nameSortField}
      onValueChange={(value) => {
        setNameSortField(value as AttendanceNameSortField);
        setSorting([{ id: 'student', desc: false }]);
      }}
    >
      <SelectTrigger className='h-9 w-full bg-secondary text-sm text-secondary-foreground sm:w-[200px]'>
        <SelectValue placeholder='Sort by last name' />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value='lastName'>Sort by last name</SelectItem>
        <SelectItem value='firstName'>Sort by first name</SelectItem>
      </SelectContent>
    </Select>
  );

  // ENR-3438: always show Trial Students section, even when empty.
  return (
    <div className=''>
      <h2 className='mb-4 text-xl font-semibold'>Trial Students</h2>

      {!hasAnyTrials ? (
        <p className='py-3 text-sm text-muted-foreground'>
          No trial students for this class date.
        </p>
      ) : (
        <>
          {/* Mobile view */}
          <div className='md:hidden'>
            <div className='mb-4 flex flex-col gap-3'>
              <EnrollioTableSearch
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder='Search trial students...'
              />
              <div className='flex flex-col gap-3 sm:flex-row sm:items-center'>
                {attendanceTypeSelect}
                {nameSortSelect}
              </div>
            </div>
            {mobileSortedData.map((trialStudent) => (
              <TrialStudentCard
                key={trialStudent._id}
                student={trialStudent}
                classId={classId}
                formattedDate={formattedDate}
              />
            ))}

            <div className='mt-4 text-sm text-muted-foreground'>
              Showing {mobileSortedData.length} of {mobileSortedData.length}{' '}
              Students
            </div>
          </div>

          {/* Desktop view */}
          <div className='hidden md:block'>
            <EnrollioTable
              ariaLabel='Trial student attendance'
              totalLabel='Students'
              filterHeader={
                <>
                  <EnrollioTableSearch
                    value={searchQuery}
                    onChange={setSearchQuery}
                    placeholder='Search trial students...'
                  />
                  {attendanceTypeSelect}
                  {nameSortSelect}
                </>
              }
              columns={cols}
              data={mobileSortedData}
              getRowId={(row) => row._id}
              sorting={sorting}
              onSortingChange={setSorting}
              manualSorting
              pagination={pagination}
              onPaginationChange={setPagination}
              mobile={false}
            />
          </div>
        </>
      )}
    </div>
  );
}
